/**
 * Mobile-Optimized Layout (< 768px)
 * Card-based feed with single-hand touch controls, quick entry Floating Action Button (FAB),
 * summary badges, and slide-up drawer for recording blood pressure.
 */

import React, { useState } from 'react';
import {
  BloodPressureRecord,
  DailyRecordGroup as DailyRecordGroupType,
  BPStatistics,
  DateRangePreset,
  RecordFormData,
} from '@/types/record';
import { getTodayDateString } from '@/utils/dateUtils';
import { classifyBP } from '@/utils/bpClassification';
import { DailyRecordGroup } from '@/components/records/DailyRecordGroup';
import { FilterBar } from '@/components/dashboard/FilterBar';
import { QuickEntryDrawer } from './QuickEntryDrawer';
import { StampBadge } from '@/components/common/StampBadge';

export interface MobileLayoutProps {
  records: BloodPressureRecord[];
  allRecords: BloodPressureRecord[];
  dailyGroups: DailyRecordGroupType[];
  stats: BPStatistics;
  activePreset: DateRangePreset;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
  onPresetChange: (preset: DateRangePreset) => void;
  onDateRangeChange: (start: string, end: string) => void;
  onSearchChange: (query: string) => void;
  onResetFilter: () => void;
  onExportCSV: () => void;
  onCreateRecord: (data: RecordFormData) => Promise<void> | void;
  onEditRecord: (record: BloodPressureRecord) => void;
  onDeleteRecord: (id: number) => void;
  onLogout: () => void;
  isLoading?: boolean;
  className?: string;
}

export const MobileLayout: React.FC<MobileLayoutProps> = ({
  records,
  allRecords,
  dailyGroups,
  stats,
  activePreset,
  startDate,
  endDate,
  searchQuery,
  onPresetChange,
  onDateRangeChange,
  onSearchChange,
  onResetFilter,
  onExportCSV,
  onCreateRecord,
  onEditRecord,
  onDeleteRecord,
  onLogout,
  isLoading = false,
  className = '',
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerDefaultDate, setDrawerDefaultDate] = useState<string | undefined>(undefined);

  const todayStr = getTodayDateString();
  const todayGroup = dailyGroups.find((g) => g.date === todayStr);

  const handleOpenAddDrawer = (targetDate?: string) => {
    setDrawerDefaultDate(targetDate);
    setIsDrawerOpen(true);
  };

  return (
    <div className={`space-y-4 pb-24 ${className}`}>
      {/* Mobile Top Header Banner */}
      <div className="bg-paper-200 border border-paper-400 rounded-xs p-3.5 shadow-paper">
        <div className="flex items-center justify-between gap-2">
          <div>
            <span className="text-[11px] font-serif text-ink-500 uppercase tracking-widest block">
              TODAY'S CLINICAL STATUS
            </span>
            <h3 className="font-serif font-bold text-sm text-ink-900">
              {todayGroup ? `今日已量測 ${todayGroup.records.length} 次` : '今日尚未登記量測數據'}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            {todayGroup?.summary.category && (
              <StampBadge category={todayGroup.summary.category} size="sm" />
            )}
            <button
              type="button"
              onClick={onLogout}
              className="px-2.5 py-1 text-xs font-serif text-ink-700 bg-paper-100 hover:bg-paper-300 border border-paper-400 rounded-xs transition-colors"
              title="鎖定系統"
            >
              🔒 鎖定
            </button>
          </div>
        </div>

        {/* Quick Today Average Metrics */}
        {todayGroup && (
          <div className="mt-2.5 pt-2 border-t border-paper-300 grid grid-cols-3 gap-2 text-center text-xs font-mono">
            <div className="bg-white/80 p-1.5 rounded-xs border border-paper-300">
              <span className="text-[10px] text-ink-500 font-serif block">平均收縮壓</span>
              <strong className="text-red-900 text-sm">{todayGroup.summary.avg_systolic}</strong>
            </div>
            <div className="bg-white/80 p-1.5 rounded-xs border border-paper-300">
              <span className="text-[10px] text-ink-500 font-serif block">平均舒張壓</span>
              <strong className="text-blue-900 text-sm">{todayGroup.summary.avg_diastolic}</strong>
            </div>
            <div className="bg-white/80 p-1.5 rounded-xs border border-paper-300">
              <span className="text-[10px] text-ink-500 font-serif block">平均心跳</span>
              <strong className="text-emerald-900 text-sm">{todayGroup.summary.avg_pulse}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Filter and Date Presets */}
      <FilterBar
        activePreset={activePreset}
        startDate={startDate}
        endDate={endDate}
        searchQuery={searchQuery}
        totalFilteredCount={records.length}
        totalCount={allRecords.length}
        onPresetChange={onPresetChange}
        onDateRangeChange={onDateRangeChange}
        onSearchChange={onSearchChange}
        onReset={onResetFilter}
        onExportCSV={onExportCSV}
      />

      {/* Daily Groups Feed */}
      <div className="space-y-3">
        {dailyGroups.length === 0 ? (
          <div className="bg-paper-100 border border-dashed border-paper-400 rounded-xs p-6 text-center space-y-2">
            <div className="text-2xl">📝</div>
            <h4 className="font-serif font-bold text-sm text-ink-800">
              尚無符合條件之血壓紀錄
            </h4>
            <p className="font-serif text-xs text-ink-500">
              點擊下方「＋ 登錄血壓」即可開始記錄。
            </p>
            <button
              type="button"
              onClick={onResetFilter}
              className="mt-2 px-3 py-1 bg-paper-200 hover:bg-paper-300 border border-paper-400 rounded-xs text-xs font-serif font-bold text-ink-900"
            >
              重設篩選
            </button>
          </div>
        ) : (
          dailyGroups.map((group, index) => (
            <DailyRecordGroup
              key={group.date}
              date={group.date}
              formattedDateZh={group.formattedDateZh}
              records={group.records}
              summary={group.summary}
              onEditRecord={onEditRecord}
              onDeleteRecord={onDeleteRecord}
              onAddRecordForDate={(date) => handleOpenAddDrawer(date)}
              defaultExpanded={index === 0}
            />
          ))
        )}
      </div>

      {/* Floating Action Button (FAB) for Instant Quick Entry */}
      <div className="fixed bottom-5 right-5 z-40">
        <button
          type="button"
          onClick={() => handleOpenAddDrawer()}
          className="
            flex items-center gap-2 bg-ink-900 hover:bg-ink-950 text-paper-100
            px-4 py-3 rounded-full shadow-[3px_3px_0px_0px_rgba(28,25,23,0.35)]
            font-serif font-bold text-sm tracking-wider uppercase active:scale-95
            transition-transform duration-150 border-2 border-paper-300
          "
          aria-label="快速登錄血壓數據"
        >
          <span className="text-lg leading-none font-mono">+</span>
          <span>登錄血壓</span>
        </button>
      </div>

      {/* Slide-Up Quick Entry Drawer Modal */}
      <QuickEntryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSubmit={onCreateRecord}
        isLoading={isLoading}
        defaultDate={drawerDefaultDate}
      />
    </div>
  );
};
